import { QuillOptions } from 'quill';
import { ApiModelStrategy, FrameworkContractOut, ModelStrategyTypes, RegularWithdrawalFrequencies } from '../services/rest-api/rest-api';
import { InstrumentCategories } from './charts/charts';
import { RadiosKeyValueTyped } from './radio-input-group/radio-input-group';

export const defaultRiskLevels = [1, 2, 3, 4, 5, 6];
export const defaultInstrumentRiskLevels = [1, 2, 3, 4, 5, 6, 7];

export type AvailablePerformanceYears = 1 | 3 | 5;

export enum charTypes {
  'INSTRUMENTS' = 1,
  'REGION' = 2,
  'HISTORY' = 3,
}

export const availableInvestmentPeriods = [
  {
    value: '1',
    key: 'weekly',
    disabled: false,
  },
  {
    value: '4',
    key: 'monthly',
    disabled: false,
  },
  {
    value: '12',
    key: 'quarterly',
    disabled: false,
  },
];

export const newPortfolioConfig = {
  oneTimeDepositMin: 500,
  regularDepositMin: 500,
  investmentNameMaxLength: 32,
  investmentNameMinLength: 3,
  timeOfInvestmentInit: 20,
}

export const instrumentCategoryColors: Record<keyof typeof InstrumentCategories | string, string> = {
  EQUITY: '#E52713',
  BOND: '#781900',
  ALTERNATIVE: '#858585',
  COMODITY: '#3B3B3B',
  CASH: '#008000',
  MONEY: '#fc5017',
  REAL_ESTATE: '#4CBB17',
  LEVERAGED: '#32CD32',
  CRYPTOCURRENCY: '#2F7EEE',
  MULTI_ASSET: '#FFD700',
}

export enum DraftTemplateColor {
  Red = '#ef4444',
  Blue = '#3b82f6',
  Gold = '#eab308',
  Purple = '#a855f7',
  Green = '#22c55e',
  Pink = '#ec4899',
  Violet = '#9333ea',
  Orange = '#f97316',
  DarkGreen = '#15803d',
  Cyan = '#06b6d4',
}

export const DRAFT_TEMPLATE_COLOR_OPTIONS: readonly DraftTemplateColor[] = [
  DraftTemplateColor.Red,
  DraftTemplateColor.Blue,
  DraftTemplateColor.Gold,
  DraftTemplateColor.Purple,
  DraftTemplateColor.Green,
  DraftTemplateColor.Pink,
  DraftTemplateColor.Violet,
  DraftTemplateColor.Orange,
  DraftTemplateColor.DarkGreen,
  DraftTemplateColor.Cyan,
];

export const DEFAULT_DRAFT_TEMPLATE_COLOR = DRAFT_TEMPLATE_COLOR_OPTIONS[0];

export const randomColors: string[] = [
  '#262626',
  '#3fa535',
  '#b5b5b5',
]

export const maxInvestmentLength = 40; // years

export const WORKPLACE_CLIENT_MAX_CONTRACTS = 2;

export enum SortDirections {
  HIGH_RISK = 'HIGH_RISK',
  LOW_RISK = 'LOW_RISK'
};

export const lowestOptimizedWidthBreakpoint = 290; //px
export const smallPhoneWidthBreakpoint = 360; //px
export const regularPhoneWidthBreakpoint = 420; //px
// https://getbootstrap.com/docs/5.0/layout/breakpoints/
export const tabletWidthBreakpoint = 576; //px
export const desktopWidthBreakpoint = 768; //px
export const largeDesktopWidthBreakpoint = 992; //px
export const xlDesktopWidthBreakpoint = 1200; //px
// export const largeScreenWidthBreakpoint = 1400; //px

export const PUBLIC_ROUTES = {
  PUBLIC_INSTRUMENTS: 'prehled-instrumentu', // todo - translate routes
  LOGIN: 'login',
  DIP_REDIRECT: 'dip-redirect',
  COOKIE_TEXT: 'cookie-text',
  ADDITION_REDIRECT: 'addition-redirect',
  BLOG_PREVIEW: 'blog/preview',
  CLIENT_ADDITION_REDIRECT: 'client-addition-redirect',
}

export const CUSTOM_STRATEGY_GLOBAL_INDEX: ApiModelStrategy = {
  modelStrategyId: -1,
  uiOrder: 0.5,
  name: 'staticPortfolios.worldIndexes',
  description: '',
  imageUrl: '/assets/images/thematic_portfolios/world.jpg',
  iconUrl: '/assets/icons/globe.svg',
  translate: true,
  type: ModelStrategyTypes.THEMATIC_STRATEGY,
}

export const CLIENT_PEEK_ROUTE = 'client-peek';
export const WORKPLACE_INVESTMENT_DETAIL = 'workplace-investment-detail';
export const INVESTMENT_GOAL_ROUTE = 'investment-goal';
export const NEW_CLIENT_ROUTE = 'new-client';
export const BLOG_ROUTE = 'blog';
export const DASHBOARD_ROUTE = 'workplace-summary';
export const CLIENT_LIST_ROUTE = 'workplace-clients';
export const DRAFT_TYPE_SELECT_ROUTE = 'draft-type-select';
export const SENT_INVESTMENT_DRAFTS_ROUTE = 'sent-investment-drafts';
export const NEW_DRAFT_ACTION = 'newDraft';
export const CLIENT_PICKER_ROUTE = 'client-picker';
export const WITHDRAWAL_AVAILABLE_CLIENTS_ROUTE = 'withdrawal-available-clients';
export const WITHDRAWAL_FORM_ROUTE = 'regular-withdrawal-form';
export const WITHDRAWAL_CHECK_ROUTE = 'regular-withdrawal-check';
export const WITHDRAWAL_COMMENT_ROUTE = 'regular-withdrawal-comment';
export const CONSULT_DRAFT_DETAIL_ROUTE = 'consult-draft-detail';
export const DRAFT_TEMPLATES_ROUTE = 'draft-templates';
export const DRAFT_TEMPLATE_ROUTE = 'draft-template';
export const NEW_DRAFT_TEMPLATE_ROUTE = 'new'; // DRAFT_TEMPLATE_ROUTE prefix
export const DETAIL_DRAFT_TEMPLATE_ROUTE = 'detail'; // DRAFT_TEMPLATE_ROUTE prefix

export const NEW_CLIENT_ID = NaN;

export const NEW_CLIENT_CONTRACT: FrameworkContractOut = {
  frameworkContractId: NaN,
  frameworkContractNumber: '',
  dip: false,
  inLiquidation: false,
  unsignedAdditions: [],
  invSignDate: new Date().toISOString().split('T')[0],
  opfFeeImplementation: false,
}

// new client does not have real contracts but DIP switch is wanted
export const NEW_CLIENT_AVAILABLE_CONTRACTS = [
  NEW_CLIENT_CONTRACT,
  {
    ...NEW_CLIENT_CONTRACT,
    dip: true,
  },
];

export const EfektikaInstrumentWeights = {
  singleEfektikaInstrumentWeight: 98,
  efektikaCashMax: 2,
}

export const CONSULT_REDIRECT_AFTER_AUTH_ROUTES = [
  CLIENT_PEEK_ROUTE,
];

export const RESET_WITHDRAWAL_DRAFT_ROUTES = [
  DASHBOARD_ROUTE,
  CLIENT_LIST_ROUTE,
  DRAFT_TYPE_SELECT_ROUTE,
  CLIENT_PICKER_ROUTE,
  WITHDRAWAL_AVAILABLE_CLIENTS_ROUTE
];

export const EFEKTIKA_PDF = 'Podminky_EFEKTIKA';

export const availableWithdrawalPeriods: RadiosKeyValueTyped<RegularWithdrawalFrequencies>[] = [
  {
    value: RegularWithdrawalFrequencies.MONTHLY,
    key: 'MONTHLY',
    disabled: false,
  },
  {
    value: RegularWithdrawalFrequencies.QUARTERLY,
    key: 'QUARTERLY',
    disabled: false,
  },
];

// html/text field allowing user simple formatting, check npm/quill
export const EDITOR_CONFIG: QuillOptions = {
  theme: 'snow',
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['clean']
    ]
  },
  formats: [
    // comment to disable from user input (incl. copypasting)
    'background',
    'bold',
    'color',
    'font',
    'code',
    'italic',
    'link',
    'size',
    'strike',
    'script',
    'underline',
    'blockquote',
    'header',
    'indent',
    'list',
    'align',
    'direction',
    'code-block',
    'formula'
  ],
};

export const DRAFT_TEMPLATES_CONFIG = {
  nameMinLength: 3,
  nameMaxLength: 32,
  descriptionMaxLength: 2000,
  defaultName: 'Strategie 1',
}
